import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Send, Loader2, Trash2, FileText, Search, Upload,
  CheckCircle, AlertTriangle, Play, Check,
} from 'lucide-react'
import Markdown from 'react-markdown'
import { useAuthStore } from '@/store/auth'
import { cn, formatDate } from '@/lib/utils'
import { apiGet } from '@/lib/api'
import { InlineMap } from '@/components/InlineMap'
import { InlineRiskMatrix } from '@/components/InlineRiskMatrix'
import { PropertyUploadModal } from '@/components/PropertyUploadModal'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface DocFlowDocument {
  document_id?: string
  id?: string
  title: string
  filename: string
  mime_type: string
  file_size?: number
  created_at?: string
}

function fmtSize(bytes?: number) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AidaChatPage() {
  const { currentPropertyId, user } = useAuthStore()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [uploadOpen, setUploadOpen] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const currentProperty = user?.propertyAccess.find((p) => p.propertyId === currentPropertyId)?.property

  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ['aida-property-docs', currentPropertyId, search],
    queryFn: () =>
      apiGet<{ results?: DocFlowDocument[]; total: number }>(
        '/docflow/documents',
        { propertyId: currentPropertyId, ...(search ? { search } : {}) }
      ),
    enabled: !!currentPropertyId,
  })

  const docs = docsData?.results ?? []

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => { setSelected(new Set()) }, [currentPropertyId])

  function toggleDoc(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function selectAll() {
    setSelected(new Set(docs.map(d => d.document_id ?? d.id ?? '').filter(Boolean)))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!input.trim() || streaming || !currentPropertyId) return

    const userMessage = input.trim()
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }])
    setStreaming(true)
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }])

    try {
      const token = JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken
      const response = await fetch('/api/v1/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          message: userMessage,
          propertyId: currentPropertyId,
          conversationHistory: messages.slice(-10),
          documentIds: Array.from(selected),
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'AI service unavailable' }))
        setMessages((prev) => {
          const updated = [...prev]
          updated[updated.length - 1] = { role: 'assistant', content: `Error: ${err.error ?? 'Something went wrong'}` }
          return updated
        })
        setStreaming(false)
        return
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      if (!reader) throw new Error('No reader')

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const json = line.slice(6)
          try {
            const data = JSON.parse(json)
            if (data.done) break
            if (data.text) {
              setMessages((prev) => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = { ...last, content: last.content + data.text }
                return updated
              })
            }
          } catch { /* skip */ }
        }
      }
    } catch {
      setMessages((prev) => {
        const updated = [...prev]
        updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, I encountered an error connecting to Aida.' }
        return updated
      })
    } finally {
      setStreaming(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  function onUploaded() {
    queryClient.invalidateQueries({ queryKey: ['aida-property-docs', currentPropertyId] })
  }

  const selectionCount = selected.size

  return (
    <div className="flex flex-col lg:flex-row -m-4 md:-m-6 -mb-20 md:-mb-6 h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)]">
      {/* ── Document sidebar ─────────────────────────────────────────── */}
      <aside className="w-full lg:w-80 xl:w-96 flex-shrink-0 bg-white border-r border-b lg:border-b-0 border-gray-100 flex flex-col max-h-72 lg:max-h-none">
        <div className="px-4 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" /> Documents
            </h2>
            <button
              onClick={() => setUploadOpen(true)}
              className="btn-primary text-xs py-1.5 px-2.5 flex items-center gap-1"
              disabled={!currentPropertyId}
            >
              <Upload className="w-3.5 h-3.5" /> Upload
            </button>
          </div>
          <p className="text-xs text-gray-400 truncate">
            {currentProperty?.name ?? 'Select a property'}
          </p>

          <div className="mt-3 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
              className="input w-full pl-8 py-1.5 text-sm"
            />
          </div>

          {docs.length > 0 && (
            <div className="flex items-center justify-between mt-3 text-xs">
              <span className="text-gray-500">
                {selectionCount > 0
                  ? <><b className="text-purple-600">{selectionCount}</b> of {docs.length} selected</>
                  : <>{docs.length} {docs.length === 1 ? 'document' : 'documents'}</>}
              </span>
              <div className="flex items-center gap-2">
                {selectionCount > 0 && (
                  <button onClick={() => setSelected(new Set())} className="text-gray-500 hover:text-navy-800">
                    Clear
                  </button>
                )}
                <button onClick={selectAll} className="text-gray-500 hover:text-navy-800">
                  Select all
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {docsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-8 px-4">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500">No documents for this property yet.</p>
              <p className="text-xs text-gray-400 mt-1">Upload one to focus the chat.</p>
            </div>
          ) : (
            <ul className="space-y-1">
              {docs.map((doc) => {
                const id = doc.document_id ?? doc.id ?? ''
                const isSelected = selected.has(id)
                return (
                  <li key={id}>
                    <button
                      onClick={() => toggleDoc(id)}
                      className={cn(
                        'w-full text-left flex items-start gap-2 px-2 py-2 rounded-lg transition-colors',
                        isSelected ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50 border border-transparent'
                      )}
                    >
                      <div className={cn(
                        'w-4 h-4 mt-0.5 rounded flex items-center justify-center flex-shrink-0 border',
                        isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                      )}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-navy-800 truncate">{doc.title || doc.filename}</p>
                        <p className="text-xs text-gray-400 truncate">
                          {fmtSize(doc.file_size)}
                          {doc.created_at && ` • ${formatDate(doc.created_at)}`}
                        </p>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* ── Chat area ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/aida-icon.png" alt="Aida" className="w-6 h-6 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-navy-800 font-semibold text-sm">Aida</h1>
              <p className="text-xs text-gray-400 truncate">
                {selectionCount > 0
                  ? `Focused on ${selectionCount} ${selectionCount === 1 ? 'document' : 'documents'}`
                  : currentProperty
                    ? `Full property context — ${currentProperty.name}`
                    : 'Select a property to begin'}
              </p>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={() => setMessages([])}
              className="text-gray-400 hover:text-navy-800 p-1.5 rounded"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-4 max-w-4xl w-full mx-auto">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <img src="/aida-icon.png" alt="Aida" className="w-16 h-16 object-contain mx-auto mb-4" />
              <p className="text-base font-medium text-navy-800">How can I help?</p>
              <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                Ask about work orders, assets, compliance, or select documents on the left to focus the conversation.
              </p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {[
                  'Summarise this week\'s maintenance activity',
                  'Which compliance certificates expire soon?',
                  'What are the top assets by work order count?',
                  selectionCount > 0 ? 'Summarise the selected documents' : 'Are there any overdue PPMs?',
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); inputRef.current?.focus() }}
                    className="text-left text-sm text-navy-600 hover:text-navy-800 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2.5 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={cn('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-1 p-1">
                  <img src="/aida-icon.png" alt="Aida" className="w-5 h-5 object-contain" />
                </div>
              )}
              <div
                className={cn(
                  'rounded-xl px-4 py-2.5 text-sm max-w-[75%]',
                  msg.role === 'user' ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-800'
                )}
              >
                {msg.role === 'assistant' ? (
                  <div>
                    {msg.content ? (
                      <>
                        <div className="prose prose-sm prose-gray max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>li]:my-0.5 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm [&>h1]:font-semibold [&>h2]:font-semibold [&_code]:text-xs [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:rounded [&_strong]:text-navy-800 [&>blockquote]:border-l-2 [&>blockquote]:border-purple-300 [&>blockquote]:pl-2 [&>blockquote]:text-gray-500 [&_table]:text-xs [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_th]:bg-gray-100 [&_th]:text-left">
                          <Markdown components={{
                            a: ({ children }) => <span className="text-purple-600 font-medium">{children}</span>,
                            code: ({ className, children }) => {
                              const lang = className?.replace('language-', '') ?? ''
                              const text = String(children).trim()
                              if (lang === 'map') {
                                try { const d = JSON.parse(text); if (d.journey?.length) return <InlineMap data={d} /> } catch {}
                              }
                              if (lang === 'risk-matrix') {
                                try { const d = JSON.parse(text); if (d.risks?.length) return <InlineRiskMatrix data={d} /> } catch {}
                              }
                              return <code className={className}>{children}</code>
                            },
                            pre: ({ children }) => <>{children}</>,
                          }}>
                            {msg.content.replace(/\[ACTION:\w+\]/g, '')}
                          </Markdown>
                        </div>
                        <AIActionButtons content={msg.content} />
                      </>
                    ) : streaming && i === messages.length - 1 ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    ) : null}
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="p-3 md:p-4 border-t border-gray-100">
          <div className="max-w-4xl mx-auto flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectionCount > 0 ? `Ask about the ${selectionCount} selected ${selectionCount === 1 ? 'document' : 'documents'}...` : 'Ask Aida anything about this property...'}
              rows={1}
              className="input resize-none flex-1 py-2.5 text-sm"
              disabled={streaming || !currentPropertyId}
            />
            <button
              type="submit"
              disabled={!input.trim() || streaming || !currentPropertyId}
              className="btn-primary px-4"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </form>
      </div>

      {uploadOpen && currentPropertyId && (
        <PropertyUploadModal
          propertyId={currentPropertyId}
          propertyName={currentProperty?.name ?? 'property'}
          onClose={() => setUploadOpen(false)}
          onUploaded={onUploaded}
        />
      )}
    </div>
  )
}

// ── AI Action Buttons — same contract as AIChatPanel ─────────────────────────
const ACTION_CONFIG: Record<string, { label: string }> = {
  assign_work_orders: { label: 'Auto-Assign Work Orders' },
  schedule_ppm: { label: 'Generate PPM Work Orders' },
}

function AIActionButtons({ content }: { content: string }) {
  const { currentPropertyId } = useAuthStore()
  const [executing, setExecuting] = useState<string | null>(null)
  const [results, setResults] = useState<Record<string, any>>({})

  const actions = [...content.matchAll(/\[ACTION:(\w+)\]/g)].map(m => m[1])
  if (actions.length === 0) return null

  async function executeAction(action: string) {
    setExecuting(action)
    try {
      const token = JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken
      let endpoint = ''
      if (action === 'assign_work_orders') endpoint = '/api/v1/ai/actions/assign-work-orders'
      else if (action === 'schedule_ppm') endpoint = '/api/v1/ai/actions/schedule-ppm'
      else return

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ propertyId: currentPropertyId }),
      })
      const data = await res.json()
      setResults(prev => ({ ...prev, [action]: data }))
    } catch {
      setResults(prev => ({ ...prev, [action]: { error: 'Action failed' } }))
    } finally {
      setExecuting(null)
    }
  }

  return (
    <div className="mt-2 space-y-2">
      {actions.map(action => {
        const cfg = ACTION_CONFIG[action]
        if (!cfg) return null
        const result = results[action]
        return (
          <div key={action}>
            {!result ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); executeAction(action) }}
                disabled={executing === action}
                className="w-full flex items-center gap-2 px-3 py-2 bg-navy-800 text-white text-xs font-medium rounded-lg hover:bg-navy-700 transition-colors disabled:opacity-50"
              >
                {executing === action ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                {executing === action ? 'Executing...' : cfg.label}
              </button>
            ) : result.error ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5" /> {result.error}
              </div>
            ) : (
              <div className="px-3 py-2 bg-green-50 text-green-800 text-xs rounded-lg flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5" /> {result.message ?? 'Done'}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
